import Swiper from "./swiper-bundle.esm.browser.min.js";
import { mergeSwiperMousewheel } from "./swiper-mousewheel-merge.js";

const ANIMATION_CLASSES = ["scroll-trigger", "animate--slide-in"];
const ANIMATION_MOBILE_DISABLED_CLASS = "disabled-on-mobile";

function getSlideAnimTarget(slide) {
  const directDiv = slide.querySelector(":scope > div");
  if (directDiv) return directDiv;
  const first = slide.firstElementChild;
  if (first && first.tagName === "DIV") return first;
  return null;
}

function markInitialVisibleSlides(
  swiper,
  {
    disableOnMobile = false,
    cascade = true,
    animClasses = ANIMATION_CLASSES,
    mobileDisabledClass = ANIMATION_MOBILE_DISABLED_CLASS,
    cascadeAttrName = "data-cascade",
    orderVarName = "--animation-order",
    orderStart = 1,
    force = false,
  } = {},
) {
  if (!swiper || !swiper.slides || swiper.slides.length === 0) {
    console.warn("markInitialVisibleSlides(): swiper or slides doesn't exist", {
      swiper: !!swiper,
      slides: !!swiper?.slides,
      slidesLength: swiper?.slides?.length,
    });
    return;
  }

  if (swiper.__initAnimated && !force) return;

  swiper.slides.forEach((slide) => {
    const target = getSlideAnimTarget(slide);
    if (!target) return;
    target.classList.remove(
      ...animClasses,
      ...(disableOnMobile ? [mobileDisabledClass] : []),
    );
    if (cascade) {
      target.removeAttribute(cascadeAttrName);
      target.style.removeProperty(orderVarName);
    }
  });

  let visible = Array.from(swiper.slides).filter((slide) =>
    slide.classList.contains("swiper-slide-visible"),
  );

  if (visible.length === 0) {
    let inViewCount = 1;
    const spv = swiper.params?.slidesPerView;
    if (typeof spv === "number") {
      inViewCount = Math.max(1, spv);
    } else {
      try {
        inViewCount = Math.max(
          1,
          Math.round(
            typeof swiper.slidesPerViewDynamic === "function"
              ? swiper.slidesPerViewDynamic("current", true)
              : 1,
          ),
        );
      } catch {
        inViewCount = 1;
      }
    }
    const start = swiper.activeIndex ?? 0;
    visible = Array.from({ length: inViewCount })
      .map((_, i) => swiper.slides[start + i])
      .filter(Boolean);
  }

  visible.forEach((slide, i) => {
    const target = getSlideAnimTarget(slide);
    if (!target) return;

    target.classList.add(
      ...animClasses,
      ...(disableOnMobile ? [mobileDisabledClass] : []),
    );

    if (cascade) {
      target.setAttribute(cascadeAttrName, "");
      target.style.setProperty(orderVarName, String(orderStart + i));
    }
  });

  swiper.__initAnimated = true;
}

const NATIVE_PAGINATION_BULLET_SVG = `<svg width="20" height="20" viewBox="0 0 28 28"><circle class="svg__circle" cx="14" cy="14" r="12" fill="none" stroke-width="2"></circle><circle class="svg__circle-inner" cx="14" cy="14" r="5" stroke-width="2"></circle></svg>`;

if (!customElements.get("slideshow-section")) {
  customElements.define(
    "slideshow-section",
    class Slider extends HTMLElement {
      constructor() {
        super();
        this.swiper = null;
        this.configuration = null;
        this.handleKeyboard = this.handleKeyboard.bind(this);
        this._nativeHero = false;
        this._nativeScrollEl = null;
        this._nativeSlides = null;
        this._nativeBullets = null;
        this._nativeActiveIndex = -1;
        this._nativeAbort = null;
        this._nativeAutoplayId = null;
        this._nativeScrollRaf = 0;
        this._nativeScrollEndTimer = null;
        this._nativeResizeHandler = this._nativeResizeHandler.bind(this);
        this._boundMatchResolution = null;
        this.breakpoint = null;
        this.initializeOrDestroySwiperForBrands =
          this.initializeOrDestroySwiperForBrands.bind(this);
        this._progressWrapper = null;
        this._progressFill = null;
      }

      get isNativeHeroSlideshow() {
        return this.hasAttribute("data-native-scroll-slideshow");
      }

      get activeIndex() {
        if (this._nativeHero) return Math.max(0, this._nativeActiveIndex);
        return this.swiper?.activeIndex ?? 0;
      }

      _getNativeScrollIndex() {
        const el = this._nativeScrollEl;
        const slides = this._nativeSlides;
        if (!el || !slides?.length) return 0;
        const mid = el.getBoundingClientRect().left + el.clientWidth / 2;
        let best = 0;
        let bestDist = Infinity;
        slides.forEach((slide, i) => {
          const r = slide.getBoundingClientRect();
          const c = r.left + r.width / 2;
          const d = Math.abs(c - mid);
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        });
        return best;
      }

      _nativeApplyIndex(index) {
        if (!this._nativeHero) return;
        const slides = this._nativeSlides;
        if (!slides?.length) return;
        const i = Math.max(0, Math.min(index, slides.length - 1));
        if (i === this._nativeActiveIndex) return;
        this._nativeActiveIndex = i;
        this._nativeBullets?.forEach((b, j) => {
          b.classList.toggle("swiper-pagination-bullet-active", j === i);
        });
        const prev = this.querySelector(".wt-slider__nav-prev");
        const next = this.querySelector(".wt-slider__nav-next");
        if (prev) {
          prev.classList.toggle("swiper-button-disabled", i <= 0);
          prev.setAttribute("aria-disabled", i <= 0 ? "true" : "false");
        }
        if (next) {
          next.classList.toggle("swiper-button-disabled", i >= slides.length - 1);
          next.setAttribute(
            "aria-disabled",
            i >= slides.length - 1 ? "true" : "false",
          );
        }
        this._handleTabindexNative(i);
        this._syncNativeAnimations(i);
      }

      _handleTabindexNative(activeIndex) {
        const slides = this._nativeSlides;
        if (!slides?.length) return;
        const focusableSelectors =
          "a, button, input, textarea, select, [tabindex]";
        slides.forEach((slide, i) => {
          slide.querySelectorAll(focusableSelectors).forEach((el) => {
            const tab =
              i === activeIndex && !el.hasAttribute("data-omit-tabindex")
                ? "0"
                : "-1";
            el.setAttribute("tabindex", tab);
          });
        });
      }

      _syncNativeAnimations(_activeIndex) {
        const slides = this._nativeSlides;
        if (!slides?.length) return;
        slides.forEach((slide) => {
          slide.querySelectorAll(".scroll-trigger").forEach((el) => {
            el.classList.remove(
              "scroll-trigger",
              "scroll-trigger--offscreen",
              "scroll-trigger--cancel",
              "scroll-trigger--design-mode",
              "animate--slide-in",
              "animate--fade-in",
              ANIMATION_MOBILE_DISABLED_CLASS,
            );
          });
          slide.querySelectorAll(".animate--slide-in, .animate--fade-in")
            .forEach((el) => {
              el.classList.remove(
                "animate--slide-in",
                "animate--fade-in",
                ANIMATION_MOBILE_DISABLED_CLASS,
              );
            });
          const target = getSlideAnimTarget(slide);
          if (target) {
            target.classList.remove(
              ...ANIMATION_CLASSES,
              ANIMATION_MOBILE_DISABLED_CLASS,
            );
            target.removeAttribute("data-cascade");
            target.style.removeProperty("--animation-order");
          }
        });
      }

      _nativeStopAutoplay() {
        if (this._nativeAutoplayId != null) {
          clearInterval(this._nativeAutoplayId);
          this._nativeAutoplayId = null;
        }
      }

      _nativeAutoplayAllowed() {
        if (!this.configuration?.autoplay) return false;
        const w = this.configuration.pauseAutoplayBelowWidth ?? 900;
        return window.innerWidth >= w;
      }

      _nativeMaybeStartAutoplay() {
        this._nativeStopAutoplay();
        if (!this._nativeAutoplayAllowed()) return;
        if (!this._nativeSlides || this._nativeSlides.length <= 1) return;
        const delay = this.configuration.autoplayDelay || 5000;
        this._nativeAutoplayId = window.setInterval(() => {
          const last = this._nativeSlides.length - 1;
          const cur = this._getNativeScrollIndex();
          let nextIdx = cur + 1;
          if (nextIdx > last) nextIdx = 0;
          this._nativeGoToIndex(nextIdx, true);
        }, delay);
      }

      _nativeGoToIndex(i, smooth) {
        const scrollEl = this._nativeScrollEl;
        const slides = this._nativeSlides;
        if (!scrollEl || !slides?.[i]) return;
        scrollEl.scrollTo({
          left: slides[i].offsetLeft,
          behavior: smooth ? "smooth" : "auto",
        });
      }

      _nativeOnScrollRaf() {
        if (!this._nativeScrollRaf) {
          this._nativeScrollRaf = requestAnimationFrame(() => {
            this._nativeScrollRaf = 0;
            const idx = this._getNativeScrollIndex();
            this._nativeApplyIndex(idx);
            this._updateSlideshowProgressFromNative();
          });
        }
      }

      _nativeOnScrollEnd() {
        const idx = this._getNativeScrollIndex();
        this._nativeApplyIndex(idx);
        this._nativeMaybeStartAutoplay();
      }

      _nativeResizeHandler() {
        if (!this._nativeHero) return;
        this.centerNavigation();
        requestAnimationFrame(() => {
          if (this._nativeScrollEl && this._nativeSlides?.length) {
            const i = Math.max(
              0,
              Math.min(this._nativeActiveIndex, this._nativeSlides.length - 1),
            );
            this._nativeGoToIndex(i, false);
            this._updateSlideshowProgressFromNative();
          }
        });
      }

      _ensureSlideshowProgressBar() {
        let wrap = this.querySelector("[data-slideshow-progress-wrap]");
        if (!wrap) {
          wrap = document.createElement("div");
          wrap.className = "wt-slideshow-section-progress";
          wrap.setAttribute("data-slideshow-progress-wrap", "");
          wrap.setAttribute("role", "presentation");
          wrap.setAttribute("aria-hidden", "true");
          const track = document.createElement("div");
          track.className = "wt-slideshow-section-progress__track";
          const fill = document.createElement("div");
          fill.className = "wt-slideshow-section-progress__fill";
          fill.setAttribute("data-slideshow-progress-fill", "");
          track.appendChild(fill);
          wrap.appendChild(track);
          this.appendChild(wrap);
        }
        this._progressWrapper = wrap;
        this._progressFill = wrap.querySelector("[data-slideshow-progress-fill]");
      }

      _updateSlideshowProgressFromNative() {
        const fill =
          this._progressFill ||
          this.querySelector("[data-slideshow-progress-fill]");
        const wrap =
          this._progressWrapper ||
          this.querySelector("[data-slideshow-progress-wrap]");
        const scrollEl = this._nativeScrollEl;
        const slides = this._nativeSlides;
        if (!fill || !scrollEl || !slides?.length) return;
        if (slides.length <= 1) {
          fill.style.transform = "scaleX(1)";
          if (wrap) wrap.hidden = true;
          return;
        }
        if (wrap) wrap.hidden = false;
        const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
        const p = maxScroll <= 0 ? 1 : scrollEl.scrollLeft / maxScroll;
        fill.style.transform = `scaleX(${Math.min(1, Math.max(0, p))})`;
      }

      _updateSlideshowProgressFromSwiper() {
        const swiper = this.swiper;
        const fill =
          this._progressFill ||
          this.querySelector("[data-slideshow-progress-fill]");
        const wrap =
          this._progressWrapper ||
          this.querySelector("[data-slideshow-progress-wrap]");
        if (!swiper || !fill) return;
        const n = swiper.slides?.length ?? 0;
        if (n <= 1) {
          fill.style.transform = "scaleX(1)";
          if (wrap) wrap.hidden = true;
          return;
        }
        if (wrap) wrap.hidden = false;
        let p =
          typeof swiper.progress === "number" && Number.isFinite(swiper.progress)
            ? swiper.progress
            : null;
        if (p == null) {
          const maxIdx = Math.max(1, n - 1);
          const idx = swiper.params?.loop
            ? swiper.realIndex ?? swiper.activeIndex
            : swiper.activeIndex;
          p = Math.min(1, Math.max(0, (idx ?? 0) / maxIdx));
        }
        fill.style.transform = `scaleX(${Math.min(1, Math.max(0, p))})`;
      }

      _initNativeHeroSlideshow() {
        this._destroyNativeHeroSlideshow();
        this._nativeHero = true;
        this._nativeActiveIndex = -1;
        this._nativeAbort = new AbortController();
        const signal = this._nativeAbort.signal;

        const root = this.querySelector(".wt-slider__container--hero");
        const scrollEl = this.querySelector("[data-scroll-slideshow]");
        const slides = Array.from(this.querySelectorAll("[data-swiper-slide]"));
        const paginationEl = this.querySelector(".wt-slider__pagination");
        const nextEl =
          this.querySelector(".wt-slider__nav-next--featured") ||
          this.querySelector(".wt-slider__nav-next");
        const prevEl =
          this.querySelector(".wt-slider__nav-prev--featured") ||
          this.querySelector(".wt-slider__nav-prev");

        if (!scrollEl || slides.length === 0) {
          console.warn(
            "[slideshow-section] Native hero: missing scroll root or slides.",
          );
          return;
        }

        this._nativeScrollEl = scrollEl;
        this._nativeSlides = slides;

        root?.classList.remove("loading");
        scrollEl.classList.remove("loading");

        if (paginationEl && slides.length > 1) {
          paginationEl.innerHTML = "";
          slides.forEach((_, i) => {
            const span = document.createElement("span");
            span.className = `swiper-pagination-bullet${i === 0 ? " swiper-pagination-bullet-active" : ""} swiper-pagination-bullet--svg-animation`;
            span.setAttribute("tabindex", "0");
            span.setAttribute("role", "button");
            span.innerHTML = NATIVE_PAGINATION_BULLET_SVG;
            span.addEventListener(
              "click",
              () => {
                this._nativeStopAutoplay();
                this._nativeGoToIndex(i, true);
              },
              { signal },
            );
            span.addEventListener(
              "keydown",
              (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  this._nativeStopAutoplay();
                  this._nativeGoToIndex(i, true);
                }
              },
              { signal },
            );
            paginationEl.appendChild(span);
          });
          this._nativeBullets = Array.from(
            paginationEl.querySelectorAll(".swiper-pagination-bullet"),
          );
        } else {
          this._nativeBullets = null;
        }

        const onNext = () => {
          this._nativeStopAutoplay();
          const cur = this._getNativeScrollIndex();
          this._nativeGoToIndex(Math.min(cur + 1, slides.length - 1), true);
        };
        const onPrev = () => {
          this._nativeStopAutoplay();
          const cur = this._getNativeScrollIndex();
          this._nativeGoToIndex(Math.max(cur - 1, 0), true);
        };

        nextEl?.addEventListener("click", onNext, { signal });
        prevEl?.addEventListener("click", onPrev, { signal });
        nextEl?.addEventListener(
          "keydown",
          (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onNext();
            }
          },
          { signal },
        );
        prevEl?.addEventListener(
          "keydown",
          (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onPrev();
            }
          },
          { signal },
        );

        const onScroll = () => {
          this._nativeOnScrollRaf();
          clearTimeout(this._nativeScrollEndTimer);
          this._nativeScrollEndTimer = setTimeout(() => {
            this._nativeScrollEndTimer = null;
            this._nativeOnScrollEnd();
          }, 140);
        };
        scrollEl.addEventListener("scroll", onScroll, { passive: true, signal });

        scrollEl.addEventListener("scrollend", () => this._nativeOnScrollEnd(), {
          signal,
        });

        scrollEl.addEventListener(
          "mouseenter",
          () => this._nativeStopAutoplay(),
          { signal },
        );
        scrollEl.addEventListener(
          "mouseleave",
          () => this._nativeMaybeStartAutoplay(),
          { signal },
        );
        scrollEl.addEventListener(
          "pointerdown",
          () => this._nativeStopAutoplay(),
          { signal },
        );

        document.addEventListener(
          "visibilitychange",
          () => {
            if (document.hidden) this._nativeStopAutoplay();
            else this._nativeMaybeStartAutoplay();
          },
          { signal },
        );

        this._nativeApplyIndex(0);
        this.centerNavigation();
        this._nativeMaybeStartAutoplay();
        this._ensureSlideshowProgressBar();
        this._updateSlideshowProgressFromNative();
      }

      _destroyNativeHeroSlideshow() {
        if (this._nativeAbort) {
          this._nativeAbort.abort();
          this._nativeAbort = null;
        }
        this._nativeStopAutoplay();
        clearTimeout(this._nativeScrollEndTimer);
        this._nativeScrollEndTimer = null;
        if (this._nativeScrollRaf) {
          cancelAnimationFrame(this._nativeScrollRaf);
          this._nativeScrollRaf = 0;
        }
        this._nativeHero = false;
        this._nativeScrollEl = null;
        this._nativeSlides = null;
        this._nativeBullets = null;
        this._nativeActiveIndex = -1;
      }

      handleTabindex(swiper) {
        const isSlidesGroup = this.hasAttribute("data-slides-group");
        const focusableSelectors =
          "a, button, input, textarea, select, [tabindex]";
        const totalSlides = swiper.slides.length;
        const slidesPerView = swiper.params.slidesPerView;

        if (swiper && totalSlides > slidesPerView) {
          swiper.slides.forEach((slide) => {
            if (isSlidesGroup) {
              const slideRect = slide.getBoundingClientRect();
              const swiperRect = this.getBoundingClientRect();

              const isFullyVisible =
                slideRect.left >= swiperRect.left &&
                slideRect.right <= swiperRect.right &&
                slideRect.top >= swiperRect.top &&
                slideRect.bottom <= swiperRect.bottom;

              slide.querySelectorAll(focusableSelectors).forEach((el) => {
                if (isFullyVisible) {
                  el.setAttribute("tabindex", "0");
                } else {
                  el.setAttribute("tabindex", "-1");
                }
                if (el.hasAttribute("data-omit-tabindex")) {
                  el.setAttribute("tabindex", "-1");
                }
              });
            } else {
              swiper.slides.forEach((slide) => {
                slide.querySelectorAll(focusableSelectors).forEach((el) => {
                  el.setAttribute("tabindex", "-1");
                });
              });

              const activeSlide = swiper.slides[swiper.activeIndex];
              activeSlide
                ?.querySelectorAll(focusableSelectors)
                .forEach((el) => {
                  el.setAttribute(
                    "tabindex",
                    el.hasAttribute("data-omit-tabindex") ? "-1" : "0",
                  );
                });
            }
          });
        } else {
          swiper.slides.forEach((slide) => {
            slide.querySelectorAll(focusableSelectors).forEach((el) => {
              el.setAttribute(
                "tabindex",
                el.hasAttribute("data-omit-tabindex") ? "-1" : "0",
              );
            });
          });
        }
      }

      handleKeyboard(event) {
        const keyCode = event.keyCode || event.which;
        const focusedElement = document.activeElement;

        if (this._nativeHero && this._nativeScrollEl) {
          const inCarousel =
            this._nativeScrollEl === focusedElement ||
            this._nativeScrollEl.contains(focusedElement) ||
            this.contains(focusedElement);
          if (inCarousel) {
            if (keyCode === 37) {
              event.preventDefault();
              this._nativeStopAutoplay();
              const curL = this._getNativeScrollIndex();
              this._nativeGoToIndex(Math.max(curL - 1, 0), true);
            } else if (keyCode === 39) {
              event.preventDefault();
              this._nativeStopAutoplay();
              const curR = this._getNativeScrollIndex();
              const last = (this._nativeSlides?.length ?? 1) - 1;
              this._nativeGoToIndex(Math.min(curR + 1, last), true);
            }
          }
          return;
        }

        if (this.swiper && this.swiper.el.contains(focusedElement)) {
          switch (keyCode) {
            case 37:
              this.swiper.slidePrev();
              break;
            case 39:
              this.swiper.slideNext();
              break;
          }
        }
      }

      connectedCallback() {
        this.readConfiguration();
        this.centerNavigation = this.centerNavigation.bind(this);
        this.shouldSkipCenterNavMethod =
          this.dataset.skipCenterNavMethod === "true";

        if (!this._boundMatchResolution) {
          this._boundMatchResolution = this.matchResolution.bind(this);
        }

        window.addEventListener("resize", this.centerNavigation);
        window.addEventListener("keydown", this.handleKeyboard);

        if (this.isNativeHeroSlideshow) {
          window.addEventListener("resize", this._nativeResizeHandler);
          this._initNativeHeroSlideshow();
          return;
        }

        if (this.configuration?.isCssScrollReels) {
          this.querySelector(".wt-slider__container--video-carousel")?.classList.remove(
            "loading",
          );
          this.querySelector(".wt-slider__scrollport")?.classList.remove("loading");
          return;
        }

        if (window.innerWidth < 900 && !this.swiper) {
          this.swiperInitilize();
        }

        if (this.dataset.brands === "true") {
          window.addEventListener(
            "resize",
            this.initializeOrDestroySwiperForBrands,
          );
        } else if (this.configuration.enableOnMedia) {
          window.addEventListener("resize", this._boundMatchResolution);
          const mq = this.configuration.enableOnMedia;
          const safeMq =
            mq === "0" || mq === "false" ? "(max-width: 0px)" : mq;
          this.breakpoint = window.matchMedia(safeMq);
          this.matchResolution();
        } else if (!this.swiper) {
          this.swiperInitilize();
        }
      }

      disconnectedCallback() {
        window.removeEventListener(
          "resize",
          this.initializeOrDestroySwiperForBrands,
        );
        window.removeEventListener("resize", this.centerNavigation);
        window.removeEventListener("resize", this._nativeResizeHandler);
        window.removeEventListener("resize", this._boundMatchResolution);
        window.removeEventListener("keydown", this.handleKeyboard);
        this._destroyNativeHeroSlideshow();
        if (this.swiper) this.swiperDestroy();
      }

      initializeOrDestroySwiperForBrands() {
        if (window.innerWidth < 900) {
          if (!this.swiper) this.swiperInitilize();
        } else if (this.swiper) this.swiperDestroy();
      }

      centerNavigation() {
        if (window.innerWidth < 900 || this.shouldSkipCenterNavMethod) return;

        const picture = this.querySelector("picture")?.classList.contains(
          "hero__pic--mobile",
        )
          ? this.querySelectorAll("picture")[1]
          : this.querySelector("picture");

        if (picture) {
          const boundingClientRectPic = picture.getBoundingClientRect();

          const btns = this.querySelectorAll(".wt-slider__nav-btn");
          btns.forEach(
            (btn) =>
              (btn.style.top = `${22 + boundingClientRectPic.height / 2}px`),
          );
        }
      }

      _cleanupSlideAnimations(swiper) {
        swiper.slides.forEach((slide) => {
          const target = getSlideAnimTarget(slide);
          if (!target) return;
          target.classList.remove(
            ...ANIMATION_CLASSES,
            ANIMATION_MOBILE_DISABLED_CLASS,
          );
          target.removeAttribute("data-cascade");
          target.style.removeProperty("--animation-order");
        });
        swiper.__initAnimated = false;
      }

      _syncInitialAnimations(swiper, { force = false } = {}) {
        const animationEnabled = !!this.configuration.enableAnimation;
        const disableOnMobile = !!this.configuration.disableAnimationOnMobile;

        if (!animationEnabled) {
          this._cleanupSlideAnimations(swiper);
          return;
        }

        setTimeout(() => {
          markInitialVisibleSlides(swiper, { disableOnMobile, force });
        }, 0);
      }

      readConfiguration() {
        if (this.isNativeHeroSlideshow) {
          const el = this.querySelector("[data-native-slideshow-config]");
          let cfg = {};
          try {
            cfg = el?.textContent ? JSON.parse(el.textContent.trim()) : {};
          } catch {
            cfg = {};
          }
          this.configuration = {
            enableOnMedia: cfg.enableOnMedia || "(min-width: 0px)",
            enableAnimation: !!cfg.enableAnimation,
            disableAnimationOnMobile: !!cfg.disableAnimationOnMobile,
            autoplay: !!cfg.autoplay,
            autoplayDelay: cfg.autoplayDelay || 5000,
            pauseAutoplayBelowWidth: cfg.pauseAutoplayBelowWidth ?? 900,
          };
          if (this.configuration.autoplay) {
            if (window.innerWidth < this.configuration.pauseAutoplayBelowWidth) {
              this.configuration.autoplay = false;
            }
          }
          return;
        }

        if (this.hasAttribute("data-css-scroll-reels")) {
          this.configuration = { isCssScrollReels: true };
          return;
        }

        const default_configuration = {
          autoHeight: false,
          slidesPerView: 1,
          autoplay: false,
          threshold: 5,
          watchSlidesProgress: true,
          /** Native overflow scroll + snap (same feel as hero slideshow). */
          cssMode: true,
          enableAnimation: false,
          disableAnimationOnMobile: false,
          /** Off by default so wheel/trackpad scrolls the page over sliders (Swiper mousewheel calls preventDefault). */
          mousewheel: false,
          pagination: {
            el: ".swiper-pagination",
            renderBullet(index, className) {
              return `<span class="${className} swiper-pagination-bullet--svg-animation"><svg width="20" height="20" viewBox="0 0 28 28"><circle class="svg__circle" cx="14" cy="14" r="12" fill="none" stroke-width="2"></circle><circle class="svg__circle-inner" cx="14" cy="14" r="5" stroke-width="2"></circle></svg></span>`;
            },
          },
          navigation: {
            nextEl: ".wt-slider__nav-next",
            prevEl: ".wt-slider__nav-prev",
          },
          scrollbar: false,
          on: {
            afterInit: (swiper) => {
              this._syncInitialAnimations(swiper, { force: false });

              const dataSwiper = this.querySelector("[data-swiper]");
              const dataSwiperContainer = this.querySelector(
                "[data-swiper-container]",
              );
              dataSwiper?.classList.remove("loading");
              dataSwiperContainer?.classList.remove("loading");

              this.centerNavigation();
              this.handleTabindex(swiper);
            },
            resize: (swiper) => {
              this._syncInitialAnimations(swiper, { force: true });
            },
            slideChangeTransitionEnd: (swiper) => {
              this.handleTabindex(swiper);
            },
          },
        };

        const get_custom_configuration = this.querySelector(
          "[data-swiper-configuration]",
        )?.innerHTML;
        const custom_configuration = get_custom_configuration
          ? JSON.parse(get_custom_configuration)
          : {};

        this.configuration = {
          ...default_configuration,
          ...custom_configuration,
          mousewheel: Object.prototype.hasOwnProperty.call(
            custom_configuration,
            "mousewheel",
          )
            ? mergeSwiperMousewheel(custom_configuration.mousewheel)
            : false,
        };

        if (this.configuration.autoplay) {
          if (window.innerWidth < 900) {
            this.configuration = {
              ...this.configuration,
              autoplay: false,
            };
          }
        }
      }

      matchResolution() {
        if (this.breakpoint.matches === true) {
          if (!this.swiper) {
            this.swiperInitilize();
          }
        } else if (this.swiper) {
          this.swiperDestroy();
        }
      }

      _bindHorizontalAxisLock(swiper, containerEl) {
        if (!this.configuration?.cssMode) return;
        if (!containerEl?.classList.contains("wt-slider__container--featured")) {
          return;
        }
        const wrapper = swiper?.wrapperEl;
        if (!wrapper || wrapper.dataset.axisLockBound === "true") return;
        wrapper.dataset.axisLockBound = "true";
        wrapper.addEventListener(
          "scroll",
          () => {
            if (wrapper.scrollTop !== 0) wrapper.scrollTop = 0;
          },
          { passive: true },
        );
      }

      swiperInitilize() {
        if (this.swiper) return;

        const containerEl = this.querySelector("[data-swiper]");
        const wrapperEl = this.querySelector("[data-swiper-container]");
        const slideEls = this.querySelectorAll("[data-swiper-slide]");

        if (!containerEl || !wrapperEl) {
          console.warn(
            "[slideshow-section] Missing required elements for Swiper init.",
            {
              container: !!containerEl,
              wrapper: !!wrapperEl,
            },
          );
          return;
        }

        slideEls.forEach((el) => {
          el.classList.remove(
            ...ANIMATION_CLASSES,
            ANIMATION_MOBILE_DISABLED_CLASS,
          );
        });

        containerEl.classList.add("swiper", "wt-slider__container");
        wrapperEl.classList.add("swiper-wrapper", "wt-slider__wrapper");
        slideEls.forEach((el) =>
          el.classList.add("swiper-slide", "wt-slider__slide"),
        );

        const paginationEl =
          this.querySelector(".wt-slider__pagination") ||
          this.querySelector(".swiper-pagination");

        const nextEl =
          this.querySelector(".wt-slider__nav-next--featured") ||
          this.querySelector(".wt-slider__nav-next");

        const prevEl =
          this.querySelector(".wt-slider__nav-prev--featured") ||
          this.querySelector(".wt-slider__nav-prev");

        const cfg = { ...this.configuration };

        if (paginationEl || cfg.pagination) {
          const resolvedPagEl =
            paginationEl ||
            (typeof cfg.pagination?.el === "string"
              ? this.querySelector(cfg.pagination.el)
              : cfg.pagination?.el);

          if (resolvedPagEl) {
            cfg.pagination = {
              clickable: true,
              ...(cfg.pagination || {}),
              el: resolvedPagEl,
            };
          } else {
            delete cfg.pagination;
          }
        }

        if (nextEl || prevEl || cfg.navigation) {
          const resolvedNext =
            nextEl ||
            (typeof cfg.navigation?.nextEl === "string"
              ? this.querySelector(cfg.navigation.nextEl)
              : cfg.navigation?.nextEl);

          const resolvedPrev =
            prevEl ||
            (typeof cfg.navigation?.prevEl === "string"
              ? this.querySelector(cfg.navigation.prevEl)
              : cfg.navigation?.prevEl);

          if (resolvedNext && resolvedPrev) {
            cfg.navigation = {
              ...(cfg.navigation || {}),
              nextEl: resolvedNext,
              prevEl: resolvedPrev,
            };
          } else {
            delete cfg.navigation;
          }
        }

        if (cfg.scrollbar) {
          const resolvedScrollbarEl =
            typeof cfg.scrollbar.el === "string"
              ? this.querySelector(cfg.scrollbar.el)
              : cfg.scrollbar.el;
          if (resolvedScrollbarEl) {
            cfg.scrollbar = {
              ...(cfg.scrollbar || {}),
              el: resolvedScrollbarEl,
            };
          } else {
            delete cfg.scrollbar;
          }
        }

        if (
          cfg.cssMode &&
          cfg.effect &&
          String(cfg.effect).toLowerCase() !== "slide"
        ) {
          cfg.cssMode = false;
        }

        if (cfg.cssMode && cfg.scrollbar) {
          delete cfg.scrollbar;
        }

        const origOn =
          cfg.on && typeof cfg.on === "object" ? { ...cfg.on } : {};
        cfg.on = {
          ...origOn,
          afterInit: (swiper) => {
            origOn.afterInit?.(swiper);
            this._bindHorizontalAxisLock(swiper, containerEl);
            this._ensureSlideshowProgressBar();
            this._updateSlideshowProgressFromSwiper();
          },
          progress: (swiper, progress) => {
            origOn.progress?.(swiper, progress);
            this._updateSlideshowProgressFromSwiper();
          },
          slideChange: (swiper) => {
            origOn.slideChange?.(swiper);
            this._updateSlideshowProgressFromSwiper();
          },
          slideChangeTransitionEnd: (swiper) => {
            origOn.slideChangeTransitionEnd?.(swiper);
            this._updateSlideshowProgressFromSwiper();
          },
        };

        this.swiper = new Swiper(containerEl, cfg);
      }

      swiperDestroy() {
        if (!this.swiper) return;

        this.swiper.__initAnimated = false;

        this.querySelector("[data-swiper]").classList.remove(
          "swiper",
          "wt-slider__container",
        );
        this.querySelector("[data-swiper-container]").classList.remove(
          "swiper-wrapper",
          "wt-slider__wrapper",
        );
        this.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.remove("swiper-slide", "wt-slider__slide");
        });
        this.swiper.destroy();
        if (this.querySelector(".swiper-pagination"))
          this.querySelector(".swiper-pagination").innerHTML = "";
        this.swiper = null;
      }

      slideTo(slide) {
        if (this._nativeHero && this._nativeScrollEl && this._nativeSlides) {
          this._nativeStopAutoplay();
          const index = this._nativeSlides.indexOf(slide);
          if (index >= 0) this._nativeGoToIndex(index, false);
          requestAnimationFrame(() => {
            this._nativeApplyIndex(this._getNativeScrollIndex());
          });
          return;
        }
        if (!this.swiper) return;
        if (this.swiper.autoplay?.stop) this.swiper.autoplay.stop();
        const index = Array.from(slide.parentNode.children).indexOf(slide);
        this.swiper.slideTo(index);
      }
    },
  );
}
